// Cost management (admin console screen 3 of 5) — route /cost-management,
// admin-only (App.jsx's RequireRole roles={['admin']}, same pattern as
// /products and /all-requests; NOT "/cost" or "/catalog/cost" or anything
// starting with "/admin" — see AdminNav.jsx's own header comment on the
// dev-proxy/Nginx path-prefix collision this app has already hit twice).
//
// This is the one screen in the whole admin console whose entire job is
// showing cost price and supplier — intentional, per root CLAUDE.md rule #1
// ("Sales must never see cost price" — a rule about sales and accidental
// leakage, not about admin, who is allowed to see cost). To keep that
// boundary reviewable rather than merely asserted:
//   - this component is NOT imported by, and does not import, CatalogPage.jsx
//     (screen 2) or anything it imports;
//   - it reads its data through its own hook file, hooks/
//     useAdminCostManagement.js, with its own grouping function and its own
//     query-key prefix, never through useAdminCatalog.js;
//   - "cost_price"/"supplier_name"/"cost_updated_at" (and their camelCase
//     API equivalents) appear ONLY inside this file and that hook file.
//
// Ledger-style table, no data-grid library, no sidebar (root CLAUDE.md,
// admin/CLAUDE.md) — same shape as AllRequestsPage/CatalogPage. Inline edit
// per row (own local CostEditForm component below), same "expand near the
// trigger" language as CatalogPage's ProductForm/PriceTierPanel.
//
// Staleness: a cost is stale if cost_updated_at is more than
// STALE_COST_DAYS old, OR if no product_costs row exists at all (null —
// "never set", the LEFT JOIN's shape for a product nobody has priced-in
// yet). This matches settings.stale_cost_days (seeded as '30' — see
// backend/scripts/seed-dev.js) but there is no live Settings screen (#5,
// not built yet) wiring that value into the frontend yet, so the number is
// hardcoded here with this comment as the one place to swap it for a real
// setting read once that screen exists.

import { useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminCostManagementProducts,
  useUpdateProductCost,
} from '../hooks/useAdminCostManagement.js';
import { useDebouncedValue } from '../utils/useDebouncedValue.js';
import { formatDate } from '../utils/formatDate.js';
import { formatMoney } from 'shared/money.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import styles from './CostManagementPage.module.css';

// See this file's header comment — swap for settings.stale_cost_days once
// the Settings screen (admin console screen 5) exists and is wired up.
const STALE_COST_DAYS = 30;

function isStale(costUpdatedAt) {
  if (!costUpdatedAt) return true; // never set
  const updated = new Date(costUpdatedAt);
  if (Number.isNaN(updated.getTime())) return true;
  const ageMs = Date.now() - updated.getTime();
  return ageMs > STALE_COST_DAYS * 24 * 60 * 60 * 1000;
}

// Money field validation mirrored client-side from
// adminCatalogRoutes.js's own moneySchema regex (positive, at most 2
// decimal places) — a fast-feedback duplicate of the server's
// authoritative check, not a replacement for it (same relationship
// PriceTierPanel.jsx's own client validate() has to catalogService's
// server-side one).
function validateCostPrice(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return 'costManagement.form.costPriceRequired';
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(trimmed) || Number(trimmed) <= 0) {
    return 'costManagement.form.costPriceInvalid';
  }
  return null;
}

function CostEditForm({ product, onDone }) {
  const { t } = useLanguage();
  const mutation = useUpdateProductCost();
  const { toastOk, toastErr } = useActionFeedback();
  const [costPrice, setCostPrice] = useState(product.costPrice ?? '');
  const [supplierName, setSupplierName] = useState(product.supplierName ?? '');
  const [errorId, setErrorId] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorId(null);

    const clientError = validateCostPrice(costPrice.toString());
    if (clientError) {
      setErrorId(clientError);
      return;
    }

    const body = {
      costPrice: costPrice.toString().trim(),
      supplierName: supplierName.trim() || undefined,
    };

    mutation.mutate(
      { id: product.id, body },
      {
        onSuccess: () => {
          toastOk(t('costManagement.savedToast'));
          onDone();
        },
        onError: (err) => toastErr(err, ['VALIDATION_ERROR', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  return (
    <form className={styles.editForm} onSubmit={handleSubmit}>
      <label className={styles.field}>
        {t('costManagement.form.costPriceLabel')}
        <input
          type="number"
          min="0"
          step="0.01"
          value={costPrice}
          onChange={(e) => setCostPrice(e.target.value)}
          autoFocus
          className={`${styles.input} tabularNums`}
        />
      </label>

      <label className={styles.field}>
        {t('costManagement.form.supplierLabel')}
        <input
          type="text"
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
          className={styles.input}
        />
      </label>

      {errorId && <p className={styles.error}>{t(errorId)}</p>}

      <div className={styles.formActions}>
        <button type="button" className={styles.cancelButton} onClick={onDone}>
          {t('costManagement.form.cancel')}
        </button>
        <button type="submit" className={styles.saveButton} disabled={mutation.isPending}>
          {mutation.isPending && <Spinner />}{' '}
          {mutation.isPending ? t('costManagement.form.saving') : t('costManagement.form.save')}
        </button>
      </div>
    </form>
  );
}

function CostRow({ product }) {
  const { t, lang } = useLanguage();
  const [editing, setEditing] = useState(false);
  const displayName = lang === 'am' && product.nameAm ? product.nameAm : product.name;
  const stale = isStale(product.costUpdatedAt);
  const formattedUpdated = formatDate(product.costUpdatedAt, lang);
  const hasNoCost = product.costPrice === null || product.costPrice === undefined;

  return (
    <>
      <tr className={`${styles.row} ${stale ? styles.rowStale : ''}`}>
        <td className={`${styles.skuCell} tabularNums`}>{product.sku}</td>
        <td>{displayName}</td>
        <td className="tabularNums">
          {hasNoCost ? t('costManagement.notSet') : formatMoney(product.costPrice)}
        </td>
        <td>{product.supplierName || t('costManagement.supplierNotSet')}</td>
        <td>
          <div className={styles.updatedCell}>
            <span className="tabularNums">
              {formattedUpdated || t('costManagement.neverUpdated')}
            </span>
            {stale && <span className={styles.staleBadge}>{t('costManagement.stale.badge')}</span>}
          </div>
        </td>
        <td className={styles.actionsCell}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setEditing((prev) => !prev)}
          >
            {hasNoCost ? t('costManagement.actions.setCost') : t('costManagement.actions.edit')}
          </button>
        </td>
      </tr>

      {editing && (
        <tr>
          <td colSpan={6} className={styles.expandedCell}>
            <CostEditForm product={product} onDone={() => setEditing(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

export default function CostManagementPage() {
  const { t } = useLanguage();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  const search = debouncedSearch.trim() || undefined;

  const {
    data: products,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminCostManagementProducts({ search });

  const [staleOnly, setStaleOnly] = useState(false);

  const allProducts = products || [];
  const visibleProducts = useMemo(
    () => (staleOnly ? allProducts.filter((p) => isStale(p.costUpdatedAt)) : allProducts),
    [allProducts, staleOnly]
  );

  const isFiltered = Boolean(search) || staleOnly;
  const isTrulyEmpty = !isLoading && !isError && allProducts.length === 0 && !isFiltered;
  const isFilteredEmpty =
    !isLoading &&
    !isError &&
    visibleProducts.length === 0 &&
    (isFiltered || allProducts.length > 0);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('costManagement.title')}</h1>
      <p className={styles.subtitle}>{t('costManagement.subtitle')}</p>

      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="costManagement-search">
          {t('costManagement.filter.searchLabel')}
        </label>
        <input
          id="costManagement-search"
          type="text"
          className={styles.searchInput}
          placeholder={t('costManagement.filter.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <label className={styles.staleOnlyLabel}>
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(e) => setStaleOnly(e.target.checked)}
          />
          {t('costManagement.filter.staleOnlyLabel')}
        </label>
      </div>

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="costManagement.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && (
        <EmptyState titleId="costManagement.emptyTitle" bodyId="costManagement.emptyBody" />
      )}

      {isFilteredEmpty && (
        <EmptyState
          titleId="costManagement.emptyFilteredTitle"
          bodyId="costManagement.emptyFilteredBody"
        />
      )}

      {!isLoading && !isError && visibleProducts.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('costManagement.col.sku')}</th>
              <th>{t('costManagement.col.name')}</th>
              <th>{t('costManagement.col.costPrice')}</th>
              <th>{t('costManagement.col.supplier')}</th>
              <th>{t('costManagement.col.updated')}</th>
              <th>{t('costManagement.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleProducts.map((product) => (
              <CostRow key={product.id} product={product} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
